import React, { forwardRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { motion, MotionProps } from 'motion/react';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { WidthKey, WIDTH_DIMS } from './constants';
import { SettingsPanelContext } from './context';
import { settingsPanelPriority, type SettingsPanelConfig } from './SETTINGS-PANEL-CONFIG';
import { useSettingsPanelStore } from './store';

const createPanelVariants = (width: number | string) => ({
  initial: { width: 0, opacity: 0 },
  animate: { width: width, opacity: 1 },
});

const panelContainerStyles = (priority: number) => css`
  order: ${priority};
  display: flex;
  flex-direction: column;
  background-color: white;
  max-width: 100%;
  position: relative;
  height: 100%;
  overflow: hidden;

  will-change: width;
  border-left: 1px solid ${theme.colors.neutral20};
  border-top: 1px solid ${theme.colors.neutral20};
`;

const panelSectionStyles = (currentPanelWidth: number | string) => css`
  min-width: ${typeof currentPanelWidth === 'string' ? `${currentPanelWidth}` : `min(${currentPanelWidth}px, 100vw)`};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

export const closeSettingsTopBarPanelEvent = 'close-settings-top-bar-panels';

const closeSettingsTopBarPanel = () => {
  const event = new CustomEvent(closeSettingsTopBarPanelEvent);
  window.dispatchEvent(event);
};

export type SettingsPanelPortalProps = React.ComponentPropsWithoutRef<'div'> & {
  id: SettingsPanelConfig;
  width?: WidthKey;
  children: React.ReactNode;
  onClose: () => void;
  enableAnimations?: boolean;
} & MotionProps;

export const SettingsPanelPortal = forwardRef<HTMLDivElement, SettingsPanelPortalProps>(
  ({ id, width = 'small', children, onClose, enableAnimations = false, ...rest }, ref) => {
    const currentWidth = useSettingsPanelStore((state) => state.currentPanelWidths[id]);
    const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
    const { registerPanel, unregisterPanel, trayIds, setLastInteractedWith, setPanelCloseFunction, updatePanelWidth } =
      useSettingsPanelStore();
    const priority = settingsPanelPriority[id];

    useLayoutEffect(() => {
      closeSettingsTopBarPanel();
      registerPanel(id, width, onClose, priority);
      return () => {
        unregisterPanel(id);
      };
    }, []);

    const isInTray = useMemo(() => trayIds.includes(id), [trayIds, id]);

    const trayRoot = useMemo(() => document.getElementById('settings-tray-root'), []);
    const panelRoot = useMemo(() => document.getElementById('settings-panel-root'), []);
    const currentPanelWidth = useMemo(() => WIDTH_DIMS[currentWidth] ?? WIDTH_DIMS[width], [currentWidth, width]);

    const panelVariants = useMemo(
      () => createPanelVariants(isXSmall ? '100%' : currentPanelWidth),
      [currentPanelWidth, isXSmall]
    );

    const handleInteraction = useCallback(() => {
      setLastInteractedWith(id);
    }, [id]);

    useLayoutEffect(() => {
      setPanelCloseFunction(id, onClose);
    }, [onClose]);

    useLayoutEffect(() => {
      const currentWidth = useSettingsPanelStore.getState().currentPanelWidths?.[id];
      if (currentWidth !== width) {
        updatePanelWidth(id, width);
      }
    }, [width]);

    if (!trayRoot || !panelRoot) return null;

    const target = isInTray ? trayRoot : panelRoot;

    return (
      <SettingsPanelContext.Provider value={{ id, mountTargetId: target.id }}>
        {createPortal(
          <motion.div
            ref={ref}
            tabIndex={-1}
            variants={enableAnimations ? panelVariants : undefined}
            initial={enableAnimations ? 'initial' : undefined}
            animate={enableAnimations ? 'animate' : undefined}
            exit={enableAnimations ? 'initial' : undefined}
            transition={{ duration: 0.3, ease: 'linear' }}
            style={{ width: isXSmall ? '100%' : currentPanelWidth }}
            css={panelContainerStyles(priority)}
            onFocusCapture={handleInteraction}
            onPointerDown={handleInteraction}
            {...rest}
          >
            <motion.section css={panelSectionStyles(isXSmall ? '100%' : currentPanelWidth)}>{children}</motion.section>
          </motion.div>,
          target
        )}
      </SettingsPanelContext.Provider>
    );
  }
);

SettingsPanelPortal.displayName = 'SettingsPanelPortal';
